import React, { useRef, useCallback, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon Megatank — Gerçekleşen Rapor (içgörülü).
 * CanonApril2026.jsx içindeki Megatank gerçekleşen bloğunun bağımsız sayfası.
 * Canlı Meta verisi + kural-tabanlı "Genel Değerlendirme" yorumu (harici AI yok).
 */
const API_BASE = 'https://api.glowytics.com';
const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';

const mtNorm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const mtIsMatch = (name) => /valuablepaper|megatank|degerli|rival/.test(mtNorm(name));
const mtParse = (name) => {
  const n = mtNorm(name);
  const concept = /rival/.test(n) ? 'Rival' : (/valuablepaper|degerli/.test(n) ? 'Valuable Paper' : 'Megatank');
  const format = /video/.test(n) ? 'Video' : (/static|statik/.test(n) ? 'Statik' : '—');
  const audience = /office/.test(n) ? 'Office' : (/home/.test(n) ? 'Home' : 'Karma');
  return { concept, format, audience };
};

// ── Değerlendirme metni — canlı veriden türetilen yazılı yorum (kural tabanlı) ──
const _mtI = (n) => Math.round(Number(n || 0)).toLocaleString('tr-TR');
const _mtC = (n) => '₺' + Math.round(Number(n || 0)).toLocaleString('tr-TR');
const _mt2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const _mtAgg = (list) => {
  const spend = list.reduce((s, c) => s + Number(c.spend || 0), 0);
  const impr = list.reduce((s, c) => s + Number(c.impressions || 0), 0);
  const reach = list.reduce((s, c) => s + Number(c.reach || 0), 0);
  const clicks = list.reduce((s, c) => s + Number(c.clicks || 0), 0);
  return {
    spend, impr, reach, clicks,
    ctr: impr > 0 ? (clicks / impr) * 100 : 0,
    cpm: impr > 0 ? (spend / impr) * 1000 : 0,
    cpc: clicks > 0 ? spend / clicks : 0,
    freq: reach > 0 ? impr / reach : 0,
  };
};

function mtBuildInsights(camps) {
  if (!Array.isArray(camps) || camps.length === 0) return [];
  const A = _mtAgg(camps);
  const rel = (a, b) => (a > 0 && b > 0) ? (Math.max(a, b) - Math.min(a, b)) / Math.min(a, b) : 1;
  const out = [];

  out.push(
    `Dönem boyunca ${_mtC(A.spend)} medya bütçesiyle ${_mtI(A.impr)} gösterim alındı ve yaklaşık ${_mtI(A.reach)} kişiye ulaşıldı. ` +
    `Ortalama frekans ${_mt2(A.freq)}, tıklama oranı %${_mt2(A.ctr)}, bin gösterim maliyeti (CPM) ₺${_mt2(A.cpm)} seviyesinde gerçekleşti. ` +
    `Tıklama oranının bu seviyede kalması bilinirlik hedefli kurguda beklenen bir tablo; kıyas büyük ölçüde erişim ve gösterim verimi üzerinden yapıldı.`
  );

  const engagement = camps.reduce((t, c) => t + Number(c.engagement || 0), 0);
  const engRate = A.impr > 0 ? engagement / A.impr : 0;
  if (engagement > 0) {
    out.push(`Kurgunun asıl kazanımı etkileşim tarafında oldu: toplam ${_mtI(engagement)} etkileşim ve %${_mt2(engRate * 100)} etkileşim oranı, içeriğin gösterim başına güçlü bir ilgi ürettiğini ve marka hatırlanırlığına doğrudan katkı sağladığını gösteriyor.`);
  }
  if (A.cpm > 0) {
    out.push(`Erişim verimi tarafında ${_mtI(A.reach)} tekil kişiye, bin gösterim başına ₺${_mt2(A.cpm)} CPM ile ulaşıldı; bilinirlik hedefinde bu maliyet seviyesi, geniş kitleye görece uygun bir maliyetle temas edildiği anlamına geliyor.`);
  }

  const vids = camps.filter((c) => c.format === 'Video');
  const stat = camps.filter((c) => c.format === 'Statik');
  if (vids.length && stat.length) {
    const V = _mtAgg(vids), S = _mtAgg(stat);
    const clauses = [];
    let clear = false;
    if (V.cpc > 0 && S.cpc > 0) {
      const g = rel(V.cpc, S.cpc);
      if (g < 0.06) {
        clauses.push(`tıklama maliyeti iki tarafta da benzer seyretti (video ₺${_mt2(V.cpc)} · statik ₺${_mt2(S.cpc)})`);
      } else {
        if (g >= 0.15) clear = true;
        const cheaper = V.cpc < S.cpc ? 'video' : 'statik';
        clauses.push(`trafiği ${cheaper} kreatifler ${_mt2(Math.max(V.cpc, S.cpc) / Math.min(V.cpc, S.cpc))} kat daha uygun maliyetle getirdi (video ₺${_mt2(V.cpc)} · statik ₺${_mt2(S.cpc)})`);
      }
    }
    if (V.cpm > 0 && S.cpm > 0) {
      const g = rel(V.cpm, S.cpm);
      if (g < 0.06) {
        clauses.push(`CPM tarafında fark ihmal edilebilir düzeyde, iki format pratikte başabaş (video ₺${_mt2(V.cpm)} · statik ₺${_mt2(S.cpm)})`);
      } else {
        if (g >= 0.15) clear = true;
        const better = V.cpm < S.cpm ? 'video' : 'statik';
        clauses.push(`gösterim maliyetinde ${better} ${g < 0.15 ? 'bir miktar' : 'belirgin şekilde'} daha uygun (video ₺${_mt2(V.cpm)} · statik ₺${_mt2(S.cpm)})`);
      }
    }
    if (clauses.length) {
      const lead = clear ? 'Format tarafında video ve statik kreatifler ayrıştı: ' : 'Video ve statik kreatifler genel olarak birbirine yakın kaldı; ';
      out.push(lead + clauses.join(', ') + '.');
    }
  }

  const riv = camps.filter((c) => c.concept === 'Rival');
  const vp = camps.filter((c) => c.concept === 'Valuable Paper');
  if (riv.length && vp.length) {
    const R = _mtAgg(riv), P = _mtAgg(vp);
    const bits = [];
    if (R.cpm > 0 && P.cpm > 0) {
      const g = rel(R.cpm, P.cpm);
      if (g < 0.06) bits.push(`gösterim maliyetleri neredeyse eşit (Rival ₺${_mt2(R.cpm)} · Valuable Paper ₺${_mt2(P.cpm)})`);
      else { const eff = R.cpm < P.cpm ? 'Rival' : 'Valuable Paper'; bits.push(`gösterim başına maliyette ${eff} ${g < 0.15 ? 'hafif' : 'belirgin'} önde (Rival ₺${_mt2(R.cpm)} · Valuable Paper ₺${_mt2(P.cpm)})`); }
    }
    if (R.ctr > 0 && P.ctr > 0) {
      const g = rel(R.ctr, P.ctr);
      if (g < 0.06) bits.push(`tıklama oranları da yakın (%${_mt2(R.ctr)} · %${_mt2(P.ctr)})`);
      else bits.push(`tıklama oranında ${R.ctr > P.ctr ? 'Rival' : 'Valuable Paper'} önde (%${_mt2(R.ctr)} · %${_mt2(P.ctr)})`);
    }
    out.push(`Konsept bazında ${_mtI(R.impr)} gösterimlik Rival ve ${_mtI(P.impr)} gösterimlik Valuable Paper birbirine yakın hacimde ilerledi` + (bits.length ? `; ${bits.join(', ')}.` : '.'));
  }

  const share = (list) => A.spend > 0 ? (list.reduce((t, c) => t + Number(c.spend || 0), 0) / A.spend) * 100 : 0;
  const rivalC = camps.filter((c) => c.concept === 'Rival');
  const vpC = camps.filter((c) => c.concept === 'Valuable Paper');
  if (rivalC.length && vpC.length && A.spend > 0) {
    out.push(`Bütçe dağılımında harcamanın %${_mt2(share(rivalC))}'i Rival, %${_mt2(share(vpC))}'i Valuable Paper kurgusuna ayrıldı; iki konsept de bilinirlik kırılımında dengeli bir ağırlıkla yürütüldü.`);
  }

  const wr = camps.map((c) => {
    const impr = Number(c.impressions || 0), clicks = Number(c.clicks || 0), spend = Number(c.spend || 0), reach = Number(c.reach || 0);
    return { ...c, _ctr: impr > 0 ? (clicks / impr) * 100 : 0, _cpm: impr > 0 ? (spend / impr) * 1000 : 0, _freq: reach > 0 ? impr / reach : 0 };
  });
  const best = [...wr].filter((c) => c._ctr > 0).sort((a, b) => b._ctr - a._ctr)[0];
  const pricey = [...wr].filter((c) => c._cpm > 0).sort((a, b) => b._cpm - a._cpm)[0];
  if (best) {
    let s = `Kreatif kırılımında en yüksek ilgiyi "${best.campaign_name}" topladı (%${_mt2(best._ctr)} tıklama oranı).`;
    if (pricey && pricey.campaign_id !== best.campaign_id && A.cpm > 0 && pricey._cpm > A.cpm * 1.2) {
      s += ` "${pricey.campaign_name}" ise ₺${_mt2(pricey._cpm)} CPM ile ortalamanın (₺${_mt2(A.cpm)}) belirgin üzerinde kaldı; bu kırılımda hedefleme daraltması ya da kreatif tazelemesi denenebilir.`;
    }
    out.push(s);
  }

  const hf = wr.filter((c) => c._freq >= 2.4).sort((a, b) => b._freq - a._freq)[0];
  if (hf) {
    out.push(
      `Sıklık tarafında "${hf.campaign_name}" ${_mt2(hf._freq)} frekansla erişim doygunluğuna yaklaştı. ` +
      `Aynı kitleye tekrar gösterim arttığından, kalan bütçeyi taze/benzer kitleye kaydırmak erişim verimini korumak için yerinde olur.`
    );
  }

  out.push(
    `Genel tabloda kampanya, awareness hedefine uygun biçimde yüksek erişim ve güçlü etkileşim üretti. ` +
    `Önümüzdeki dönemde en verimli çalışan format ve kurguya ağırlık vererek, aynı bütçeyle daha fazla nitelikli erişim ve etkileşim hedeflenebilir.`
  );

  return out;
}


// ── ACTIVITY PERFORMANCE slaytı (Çekim Atölyesi düzeni) — Megatank ───────────
const CANON_RED = '#CC0000';
const CANON_RED_DARK = '#A30000';
const _fmtBig = (n) => {
  if (n == null) return '–';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1).replace('.0', '')}K`;
  return new Intl.NumberFormat('tr-TR').format(Math.round(n));
};
const _fmtMoney = (n, ccy) => {
  if (n == null) return '–';
  try { return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: ccy || 'TRY', maximumFractionDigits: 0 }).format(n); }
  catch { return `${Math.round(n).toLocaleString('tr-TR')} ${ccy || ''}`.trim(); }
};
const _fmtPct = (n, d = 2) => (n == null ? '–' : `${(n * 100).toFixed(d)}%`);
const _TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const _trLongDate = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s || '';
  return `${Number(m[3])} ${_TR_MONTHS[Number(m[2]) - 1]} ${m[1]}`;
};

function buildActivityNarrative(kpi, count, dr) {
  const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
  const money = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const L = [];
  L.push(`Kampanya ${count} kampanya seçili olarak yürütüldü.`);
  if (dr?.from && dr?.to) L.push(`${_trLongDate(dr.from)} ile ${_trLongDate(dr.to)} tarihleri arasında toplam ${nf(kpi.paidImpressions)} gösterim elde edildi.`);
  else L.push(`Toplam ${nf(kpi.paidImpressions)} gösterim elde edildi.`);
  L.push(`Yaklaşık ${nf(kpi.paidReach)} kişiye ulaşıldı.`);
  L.push(`Toplam etkileşim ${nf(kpi.paidEngagement)} olarak gerçekleşti.`);
  if (kpi.engagementRate != null) L.push(`Etkileşim oranı %${(kpi.engagementRate * 100).toFixed(2)} olarak belirlendi.`);
  if (kpi.ctr != null) L.push(`Tıklama oranı %${(kpi.ctr * 100).toFixed(2)} olarak kaydedildi.`);
  L.push(`Kampanya için toplam bütçe ${money(kpi.cost != null ? kpi.cost : kpi.budget)} TL olarak belirlendi.`);
  if (kpi.postCount != null) L.push(`Toplam ${nf(kpi.postCount)} reklam (post) kullanıldı.`);
  return L.map((l) => `- ${l}`).join('\n');
}

function MegatankActivitySlide({ mtRep, mtMedia, creatives = [], slideRef }) {
  // Üstteki "Gerçekleşen" raporla BİREBİR aynı canlı kaynak (campaign-insights).
  const camps = (mtRep?.campaigns || []).filter((c) => mtIsMatch(c.campaign_name));
  const loading = !mtRep;
  const count = camps.length;
  const dr = mtRep?.window ? { from: mtRep.window.since, to: mtRep.window.until } : null;

  const sum = (kk) => camps.reduce((t, c) => t + Number(c[kk] || 0), 0);
  const impressions = sum('impressions');
  const reach = sum('reach');
  const clicks = sum('clicks');
  const spend = sum('spend');
  const engagement = sum('engagement');
  const engagementRate = impressions > 0 ? engagement / impressions : 0;
  const ctr = impressions > 0 ? clicks / impressions : 0;
  const cpc = clicks > 0 ? spend / clicks : 0;
  const cpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
  const freq = reach > 0 ? impressions / reach : 0;
  const postCount = (mtMedia?.campaigns || []).reduce((t, c) => t + (c.creatives || []).length, 0) || count;

  const money2 = (n) => '₺' + Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const tiles = [
    { label: 'PAID\nIMPRESSIONS', value: _fmtBig(impressions) },
    { label: 'PAID REACH', value: _fmtBig(reach), hint: 'yaklaşık tekil kişi' },
    { label: 'FREKANS', value: freq ? freq.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '–' },
    { label: 'PAID\nENGAGEMENT', value: _fmtBig(engagement) },
    { label: 'ENGAGEMENT\nRATE', value: _fmtPct(engagementRate) },
    { label: 'TIKLAMA', value: _fmtBig(clicks) },
    { label: 'CTR', value: _fmtPct(ctr) },
    { label: 'CPC', value: cpc ? money2(cpc) : '–' },
    { label: 'CPM', value: cpm ? money2(cpm) : '–' },
    { label: 'BUDGET', value: _fmtMoney(spend, 'TRY'), hint: 'planlanan' },
    { label: 'COST', value: _fmtMoney(spend, 'TRY'), hint: 'gerçekleşen' },
    { label: 'POST', value: new Intl.NumberFormat('tr-TR').format(postCount) },
  ];
  const note = count ? buildActivityNarrative({ paidImpressions: impressions, paidReach: reach, paidEngagement: engagement, engagementRate, ctr, cost: spend, postCount }, count, dr) : '';
  const heroes = (creatives || []).slice(0, 2);

  if (!loading && !count) return null;

  return (
    <div className="pt-8 border-t-4 border-red-200 space-y-3">
      <div ref={slideRef} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 16, padding: 32, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `3px solid ${CANON_RED}`, paddingBottom: 12 }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: 0.5 }}>MEGATANK</div>
            <div style={{ fontSize: 14, color: '#555', marginTop: 4, letterSpacing: 1 }}>ACTIVITY PERFORMANCE</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 12, color: '#888' }}>
            {dr ? `${dr.from} → ${dr.to}` : ''}
            <div>{count ? `${count} kampanya seçili` : ''}</div>
          </div>
        </div>

        {loading && <div style={{ marginTop: 16, color: '#888', fontSize: 13 }}>Activity performansı çekiliyor…</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 32, marginTop: 24 }}>
          <div>
            {/* KPI tiles — Megatank kampanyalarının tüm metrikleri */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, rowGap: 14 }}>
              {tiles.map((t, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    background: CANON_RED, color: 'white', width: '100%', aspectRatio: '1 / 1', borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, textAlign: 'center',
                    whiteSpace: 'pre-line', fontWeight: 700, padding: 4, boxShadow: 'inset 0 0 0 1px ' + CANON_RED_DARK,
                  }}>{t.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111', marginTop: 4, textAlign: 'center' }}>{t.value}</div>
                  {t.hint && <div style={{ fontSize: 9, color: '#888', textAlign: 'center' }}>{t.hint}</div>}
                </div>
              ))}
            </div>

            {/* Overall Insights (deterministik, düzenlenebilir) */}
            <div style={{ marginTop: 28 }}>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Overall Insights</div>
              {note ? (
                <div style={{ fontSize: 13, color: '#333', lineHeight: 1.7 }}>
                  {note.split('\n').map((line, i) => (
                    <div key={i}>{line.replace(/^-\s*/, '')}</div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#888', fontSize: 13, fontStyle: 'italic' }}>Veri yüklendiğinde değerlendirme otomatik oluşturulur.</div>
              )}
            </div>
          </div>

          {/* Creatives (canlı Megatank kreatifleri — örnek görsel) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[0, 1].map((idx) => {
              const url = heroes[idx];
              return (
                <div key={idx} style={{ position: 'relative', aspectRatio: '4 / 5', borderRadius: 6, overflow: 'hidden', background: '#f5f5f5', border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {url
                    ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                    : <span style={{ fontSize: 10, color: '#bbb' }}>kreatif</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #eee', marginTop: 24, paddingTop: 12 }}>
          <img src="/Canon_logo_vector.png" alt="Canon" style={{ height: 34, width: 'auto', objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 10, color: '#888', textAlign: 'right' }}>{new Date().toLocaleDateString('en-US')} · R1 · Confidential</div>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" style={{ height: 30, width: 'auto', objectFit: 'contain' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CanonMegatankReport() {
  const mtReportRef = useRef(null);
  const mtMediaRef = useRef(null);
  const mtActivityRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const [mtRange, setMtRange] = useState('all');
  const [mtRep, setMtRep] = useState(null);
  const [mtLoading, setMtLoading] = useState(false);
  const [mtErr, setMtErr] = useState(null);

  const fmtNum = (n) => Number(n).toLocaleString('tr-TR');
  const fmtCur = (n) => '₺' + Number(n).toLocaleString('tr-TR');

  const loadMtReport = useCallback(async () => {
    setMtLoading(true); setMtErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/meta-ads/live/campaign-insights?dateRange=${encodeURIComponent(mtRange)}`, {
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        credentials: 'include',
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `İstek başarısız (${res.status})`);
      }
      setMtRep(await res.json());
    } catch (e) {
      setMtErr(e.message || 'Canlı veri çekilemedi');
    } finally {
      setMtLoading(false);
    }
  }, [mtRange]);

  useEffect(() => { loadMtReport(); }, [loadMtReport]);

  // Medya + hedef kitle
  const [mtMedia, setMtMedia] = useState(null);
  const [mtMediaLoading, setMtMediaLoading] = useState(false);
  const [mtMediaErr, setMtMediaErr] = useState(null);

  useEffect(() => {
    const ids = (mtRep?.campaigns || [])
      .filter((c) => mtIsMatch(c.campaign_name))
      .map((c) => c.campaign_id)
      .filter(Boolean);
    if (!ids.length) { setMtMedia(null); return; }
    let cancelled = false;
    (async () => {
      setMtMediaLoading(true); setMtMediaErr(null);
      try {
        const res = await fetch(
          `${API_BASE}/api/meta-ads/live/campaign-media-audience?campaignIds=${encodeURIComponent(ids.join(','))}`,
          { headers: { 'X-Brand-ID': CANON_BRAND_ID }, credentials: 'include' }
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || `İstek başarısız (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) setMtMedia(data);
      } catch (e) {
        if (!cancelled) setMtMediaErr(e.message || 'Medya/kitle çekilemedi');
      } finally {
        if (!cancelled) setMtMediaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mtRep]);

  const inlineImagesAsDataUrls = useCallback(async (root) => {
    if (!root) return [];
    const imgs = Array.from(root.querySelectorAll('img')).filter((im) => /^https?:/i.test(im.src));
    const originals = imgs.map((im) => ({ im, src: im.src }));
    const uniqueUrls = [...new Set(imgs.map((im) => im.src))];
    const map = {};
    for (let i = 0; i < uniqueUrls.length; i += 10) {
      const batch = uniqueUrls.slice(i, i + 10);
      try {
        const res = await fetch(`${API_BASE}/api/ai/image-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ urls: batch }),
        });
        const data = await res.json();
        for (const r of (data.results || [])) {
          if (r.success && r.base64) map[r.url] = `data:${r.contentType || 'image/jpeg'};base64,${r.base64}`;
        }
      } catch (_) { /* yoksay */ }
    }
    for (const { im, src } of originals) { if (map[src]) im.src = map[src]; }
    await Promise.all(originals.map(({ im }) => (im.complete ? Promise.resolve() : new Promise((r) => { im.onload = im.onerror = r; }))));
    return originals;
  }, []);

  const handleExportPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    let restore = [];
    try {
      const margin = 10;
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;

      restore = await inlineImagesAsDataUrls(mtMediaRef.current);

      let anyPage = false;
      const addElementPaged = async (el) => {
        if (!el) return;
        const canvas = await html2canvas(el, {
          scale: 3, useCORS: true, logging: false, backgroundColor: '#ffffff',
          scrollX: 0, scrollY: 0, windowWidth: el.scrollWidth, windowHeight: el.scrollHeight,
        });
        const pxPerMm = canvas.width / usableW;
        const pageHpx = usableH * pxPerMm;
        let offset = 0;
        while (offset < canvas.height) {
          const slicePx = Math.min(pageHpx, canvas.height - offset);
          const slice = document.createElement('canvas');
          slice.width = canvas.width;
          slice.height = Math.round(slicePx);
          const ctx = slice.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, slice.width, slice.height);
          ctx.drawImage(canvas, 0, offset, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
          const imgData = slice.toDataURL('image/jpeg', 0.95);
          if (anyPage) pdf.addPage();
          anyPage = true;
          pdf.addImage(imgData, 'JPEG', margin, margin, usableW, slicePx / pxPerMm);
          offset += slicePx;
        }
      };

      await addElementPaged(mtReportRef.current);
      await addElementPaged(mtMediaRef.current);
      await addElementPaged(mtActivityRef.current);
      pdf.save('Canon_Megatank_Gerceklesen_Rapor.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      for (const { im, src } of restore) im.src = src;
      setExporting(false);
    }
  }, [exporting, inlineImagesAsDataUrls]);

  // Slaytın sağ sütunu için canlı Megatank kreatiflerinden ilk 2 STATİK görsel.
  // Video posterleri siyah/boş kare geldiğinden video kreatifleri atlanır.
  const heroCreatives = (mtMedia?.campaigns || [])
    .flatMap((c) => (c.creatives || []))
    .filter((cr) => cr.type !== 'video')
    .map((cr) => cr.image_url || cr.thumbnail_url)
    .filter(Boolean)
    .slice(0, 2);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-8">
      {/* Toolbar */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600">Gerçekleşen veri aralığı</label>
          <select
            value={mtRange}
            onChange={(e) => setMtRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">Kampanya ömrü (tümü)</option>
            <option value="last90">Son 90 gün</option>
            <option value="last30">Son 30 gün</option>
            <option value="lastMonth">Geçen ay</option>
          </select>
          <button
            onClick={loadMtReport}
            disabled={mtLoading}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {mtLoading ? 'Çekiliyor…' : 'Yenile'}
          </button>
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
        >
          {exporting ? 'PDF Oluşturuluyor...' : 'Gerçekleşen Rapor + Kreatifler PDF'}
        </button>
      </div>

      {/* Gerçekleşen rapor */}
      <div ref={mtReportRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-6">
        <div className="flex items-start gap-3">
          <span style={{ display: 'inline-block', backgroundColor: '#059669', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>GERÇEKLEŞEN</span>
          <div>
            <p className="text-sm font-bold text-gray-800">Megatank Bilinirlik Kampanyası — Gerçekleşen Rapor</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Meta Ads (FB + IG) · Kampanya isminden eşleştirilen <strong>canlı</strong> veriler
              {mtRep?.window ? ` · ${mtRep.window.since} → ${mtRep.window.until}` : ''}
              {mtRep?.accountId ? ` · ${mtRep.accountId}` : ''}
            </p>
          </div>
        </div>

        {mtErr && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{mtErr}</div>
        )}
        {mtLoading && !mtRep && (
          <div className="text-sm text-gray-500 py-6 text-center">Canlı veri çekiliyor…</div>
        )}

        {mtRep && (() => {
          const sumK = (arr, k) => arr.reduce((t, c) => t + Number(c[k] || 0), 0);
          const camps = (mtRep.campaigns || [])
            .filter((c) => mtIsMatch(c.campaign_name))
            .map((c) => ({ ...c, ...mtParse(c.campaign_name) }))
            .sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));

          if (!camps.length) {
            return <div className="text-sm text-gray-500 py-6 text-center">Bu aralıkta eşleşen Megatank kampanyası bulunamadı.</div>;
          }

          const withRatios = (o) => ({
            ...o,
            ctr: o.impressions > 0 ? (o.clicks / o.impressions) * 100 : 0,
            cpm: o.impressions > 0 ? (o.spend / o.impressions) * 1000 : 0,
            cpc: o.clicks > 0 ? o.spend / o.clicks : 0,
          });
          const act = withRatios({
            spend: sumK(camps, 'spend'), impressions: sumK(camps, 'impressions'),
            reach: sumK(camps, 'reach'), clicks: sumK(camps, 'clicks'),
          });
          const rivalCamps = camps.filter((c) => c.concept === 'Rival');
          const vpCamps = camps.filter((c) => c.concept === 'Valuable Paper');
          const otherCamps = camps.filter((c) => c.concept !== 'Rival' && c.concept !== 'Valuable Paper');

          const fmt2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

          const KPIS = [
            { label: 'Toplam Harcama', val: fmtCur(Math.round(act.spend)), sub: `${camps.length} kampanya`, color: 'emerald' },
            { label: 'Gösterim', val: fmtNum(Math.round(act.impressions)), sub: `CPM ₺${fmt2(act.cpm)}`, color: 'sky' },
            { label: 'Erişim', val: fmtNum(Math.round(act.reach)), sub: `Sıklık ${fmt2(act.reach > 0 ? act.impressions / act.reach : 0)}`, color: 'violet' },
            { label: 'Tıklama', val: fmtNum(Math.round(act.clicks)), sub: `CTR %${fmt2(act.ctr)} · CPC ₺${fmt2(act.cpc)}`, color: 'amber' },
          ];
          const KPI_BG = { emerald: 'bg-emerald-50 border-emerald-200', sky: 'bg-sky-50 border-sky-200', violet: 'bg-violet-50 border-violet-200', amber: 'bg-amber-50 border-amber-200' };

          const CONCEPTS = [
            { name: 'Rival', camps: rivalCamps, badge: '#dc2626', bg: '#fef2f2', bd: '#fecaca' },
            { name: 'Valuable Paper', camps: vpCamps, badge: '#0284c7', bg: '#f0f9ff', bd: '#bae6fd' },
          ];

          const notes = mtBuildInsights(camps);

          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {KPIS.map((k) => (
                  <div key={k.label} className={`rounded-xl border p-4 ${KPI_BG[k.color]}`}>
                    <p className="text-[11px] text-gray-500">{k.label}</p>
                    <p className="text-lg font-bold text-gray-900 mt-0.5">{k.val}</p>
                    <p className="text-[10px] text-gray-500 mt-1">{k.sub}</p>
                  </div>
                ))}
              </div>

              {notes.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span style={{ display: 'inline-block', width: 3, height: 16, borderRadius: 2, backgroundColor: '#059669' }} />
                    <p className="text-sm font-bold text-gray-800">Genel Değerlendirme</p>
                  </div>
                  <div className="space-y-2.5">
                    {notes.map((p, i) => (
                      <p key={i} className="text-[12.5px] leading-relaxed text-gray-700">{p}</p>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CONCEPTS.map((cn) => {
                  const cSpend = sumK(cn.camps, 'spend');
                  const cImpr = sumK(cn.camps, 'impressions');
                  return (
                    <div key={cn.name} className="rounded-xl border p-4" style={{ borderColor: cn.bd, backgroundColor: cn.bg }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span style={{ display: 'inline-block', backgroundColor: cn.badge, color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{cn.name}</span>
                        <span className="text-[11px] text-gray-500">{cn.camps.length} kampanya</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900 mt-1">{fmtCur(Math.round(cSpend))}</p>
                      <p className="text-[10px] text-gray-500 mt-1">{fmtNum(Math.round(cImpr))} gösterim</p>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-xl border overflow-hidden">
                <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Kampanya Detayı — Gerçekleşen</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Kampanya', 'Konsept', 'Format', 'Harcama', 'Gösterim', 'Erişim', 'Tıklama', 'CTR', 'CPM'].map((h, i) => (
                          <th key={h} className={`px-3 py-2 text-[10px] font-medium text-gray-500 uppercase tracking-wider ${i < 3 ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {camps.map((c) => {
                        const d = withRatios({
                          spend: Number(c.spend || 0), impressions: Number(c.impressions || 0),
                          reach: Number(c.reach || 0), clicks: Number(c.clicks || 0),
                        });
                        return (
                          <tr key={c.campaign_id || c.campaign_name} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 text-[11px] text-gray-700 max-w-[260px] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                            <td className="px-3 py-2.5 text-[11px]">
                              <span style={{ display: 'inline-block', backgroundColor: c.concept === 'Rival' ? '#dc2626' : (c.concept === 'Valuable Paper' ? '#0284c7' : '#6b7280'), color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{c.concept}</span>
                              {c.audience !== 'Karma' && <span className="ml-1 text-gray-500">{c.audience}</span>}
                            </td>
                            <td className="px-3 py-2.5 text-[11px] text-gray-600">{c.format}</td>
                            <td className="px-3 py-2.5 text-xs text-right tabular-nums font-semibold text-gray-800">{fmtCur(Math.round(d.spend))}</td>
                            <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">{fmtNum(d.impressions)}</td>
                            <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">{fmtNum(d.reach)}</td>
                            <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">{fmtNum(d.clicks)}</td>
                            <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">%{fmt2(d.ctr)}</td>
                            <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-600">₺{fmt2(d.cpm)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-800 text-white font-bold">
                        <td className="px-3 py-2.5 text-xs" colSpan={3}>Toplam ({camps.length} kampanya)</td>
                        <td className="px-3 py-2.5 text-xs text-right">{fmtCur(Math.round(act.spend))}</td>
                        <td className="px-3 py-2.5 text-xs text-right">{fmtNum(Math.round(act.impressions))}</td>
                        <td className="px-3 py-2.5 text-xs text-right">{fmtNum(Math.round(act.reach))}</td>
                        <td className="px-3 py-2.5 text-xs text-right">{fmtNum(Math.round(act.clicks))}</td>
                        <td className="px-3 py-2.5 text-xs text-right">%{fmt2(act.ctr)}</td>
                        <td className="px-3 py-2.5 text-xs text-right">₺{fmt2(act.cpm)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {otherCamps.length > 0 && (
                <p className="text-[11px] text-gray-400">
                  Not: {otherCamps.length} kampanya "Megatank" genel etiketiyle eşleşti (Rival/Valuable Paper ayrımı isimden çıkarılamadı).
                </p>
              )}
            </>
          );
        })()}
      </div>

      {/* Medya + hedef kitle */}
      <div className="pt-2">
        <div ref={mtMediaRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden p-6 space-y-5">
          <div className="flex items-start gap-3">
            <span style={{ display: 'inline-block', backgroundColor: '#4f46e5', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>MEDYA</span>
            <div>
              <p className="text-sm font-bold text-gray-800">Kampanya Bazında Kullanılan Medya</p>
              <p className="text-xs text-gray-500 mt-0.5">Her Megatank kampanyasının kreatifleri ve metrikleri — <strong>canlı</strong> Meta Graph API.</p>
            </div>
          </div>

          {mtMediaErr && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{mtMediaErr}</div>
          )}
          {mtMediaLoading && !mtMedia && (
            <div className="text-sm text-gray-500 py-6 text-center">Medya & hedef kitle çekiliyor…</div>
          )}
          {!mtMediaLoading && !mtMedia && !mtMediaErr && (
            <div className="text-sm text-gray-500 py-6 text-center">Önce yukarıdaki gerçekleşen rapor yüklenmeli.</div>
          )}

          {mtMedia && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {(mtMedia.campaigns || []).map((c) => {
                const m = (mtRep?.campaigns || []).find((x) => x.campaign_id === c.campaign_id) || {};
                const fmt2 = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const stats = [
                  { label: 'Harcama', val: fmtCur(Math.round(Number(m.spend || 0))) },
                  { label: 'Gösterim', val: fmtNum(Math.round(Number(m.impressions || 0))) },
                  { label: 'Erişim', val: fmtNum(Math.round(Number(m.reach || 0))) },
                  { label: 'Tıklama', val: fmtNum(Math.round(Number(m.clicks || 0))) },
                  { label: 'CTR', val: `%${fmt2(m.ctr)}` },
                  { label: 'CPM', val: `₺${fmt2(m.cpm)}` },
                ];
                return (
                  <div key={c.campaign_id} className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2.5 border-b border-gray-100">
                      <p className="text-[11px] font-mono text-gray-700 truncate" title={c.campaign_name}>{c.campaign_name || c.campaign_id}</p>
                    </div>
                    {c.error ? (
                      <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50">Çekilemedi: {c.error}</div>
                    ) : (
                      <div className="p-4 space-y-3">
                        {(c.creatives || []).length === 0 ? (
                          <p className="text-[11px] text-gray-400">Kreatif bulunamadı.</p>
                        ) : (
                          <div className="flex flex-wrap justify-center gap-2">
                            {(c.creatives || []).map((cr) => (
                              <div key={cr.ad_id} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100" title={cr.ad_name || cr.creative_name || ''}>
                                {cr.image_url || cr.thumbnail_url ? (
                                  <img src={cr.image_url || cr.thumbnail_url} alt={cr.ad_name || 'kreatif'} className="h-72 w-auto object-contain" loading="lazy" />
                                ) : (
                                  <div className="h-72 w-48 flex items-center justify-center text-[10px] text-gray-400">görsel yok</div>
                                )}
                                {cr.type === 'video' && (
                                  <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[9px] px-2 py-0.5 rounded-full">▶ video</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-2 pt-1">
                          {stats.map((s) => (
                            <div key={s.label} className="rounded-lg bg-gray-50 border border-gray-100 px-2 py-2 text-center">
                              <p className="text-[9px] text-gray-400 uppercase tracking-wide">{s.label}</p>
                              <p className="text-sm font-semibold text-gray-800 tabular-nums">{s.val}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-[11px] text-gray-400">
            Kreatifler ve metrikler Meta Graph API'den canlı çekilir; görsel/video önizleme URL'leri Meta CDN'inde süreli olabilir (sayfa her açılışta yenilenir).
          </p>
        </div>
      </div>

      {/* Activity Performance slaytı (Çekim Atölyesi düzeni) — üst raporla aynı canlı veri */}
      <MegatankActivitySlide slideRef={mtActivityRef} mtRep={mtRep} mtMedia={mtMedia} creatives={heroCreatives} />
    </div>
  );
}
